import React, { Component, ReactNode, useEffect, useMemo } from 'react';
import useUpdatedRef from '@restart/hooks/useUpdatedRef';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm as useHookForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { shallowEqual } from 'react-redux';

import {
  Checkbox,
  CheckboxProps,
  FormGroup,
  Input,
  InputProps,
  Radio,
  RadioProps,
  TextArea,
  TextAreaProps,
  Toggle,
  ToggleProps,
} from '../components';

import { ConnectedAsyncSelect } from './components/statefulComponents/ConnectedAsyncSelect';
import { ConnectedAuthCodeInput } from './components/statefulComponents/ConnectedAuthCodeInput';
import { ConnectedCheckboxGroup } from './components/statefulComponents/ConnectedCheckboxGroup';
import { ConnectedPhoneNumber } from './components/statefulComponents/ConnectedPhoneNumber';
import { ConnectedRadioGroup } from './components/statefulComponents/ConnectedRadioGroup';
import { ConnectedRangePickerInput } from './components/statefulComponents/ConnectedRangePicker';
import { ConnectedSelect } from './components/statefulComponents/ConnectedSelect';
import { ConnectedSingleDayPickerInput } from './components/statefulComponents/ConnectedSingleDayPickerInput';
import { connectFormField } from './utils/connectFields/connectFormField';

type BaseFormProps = {
  className?: string;
  id?: string;
  children?: ReactNode;
};

export type FormProps<FormValues extends FieldValues> = BaseFormProps &
  UseFormProps<FormValues> & {
    /**
     * Allows to reset form state on default props change
     */
    resetOnDefaultValuesChange?: boolean;
    /**
     * Submit handler that is being passed in RHF
     * and covered with {@see SubmitHandler}
     */
    onSubmit?: SubmitHandler<FormValues>;
  };

export type FormConnectorProps<FormValues extends FieldValues> = FormProps<FormValues> & {
  children?: ReactNode;
  getFormContext(options: UseFormProps<FormValues>): UseFormReturn<FormValues>;
};

function FormConnector<FormValues extends FieldValues>({
  children,
  onSubmit,
  id,
  className,
  getFormContext,
  ...formOptions
}: FormConnectorProps<FormValues>) {
  const context = getFormContext(formOptions);
  const { handleSubmit, getValues } = context;

  return (
    <FormProvider {...context}>
      <form
        id={id}
        className={className}
        onSubmit={onSubmit && handleSubmit(onSubmit)}
        data-testid="form-component"
        onReset={() => {
          if (formOptions.defaultValues && !shallowEqual(formOptions.defaultValues, getValues())) {
            context.reset({ ...formOptions.defaultValues });
          }
        }}
      >
        {children}
      </form>
    </FormProvider>
  );
}

FormConnector.displayName = 'FormConnector';

export class Form<FormValues extends FieldValues, IsHooked = false> extends Component<
  IsHooked extends true ? BaseFormProps : FormProps<FormValues> & { onSubmit: SubmitHandler<FormValues> }
> {
  public static Group = FormGroup;
  public static Input = connectFormField<HTMLInputElement, Omit<InputProps, 'label'>>(Input);
  public static TextArea = connectFormField<HTMLTextAreaElement, Omit<TextAreaProps, 'label'>>(TextArea);
  public static Radio = connectFormField<HTMLInputElement, RadioProps>(Radio);
  public static Checkbox = connectFormField<HTMLInputElement, CheckboxProps>(Checkbox);
  public static Toggle = connectFormField<HTMLInputElement, ToggleProps>(Toggle);
  public static Select = ConnectedSelect;
  public static RadioGroup = ConnectedRadioGroup;
  public static PhoneInput = ConnectedPhoneNumber;
  public static AsyncSelect = ConnectedAsyncSelect;
  public static CheckboxGroup = ConnectedCheckboxGroup;
  public static AuthCodeInput = ConnectedAuthCodeInput;
  public static SingleDayPickerInput = ConnectedSingleDayPickerInput;
  public static RangeDayPickerInput = ConnectedRangePickerInput;

  render() {
    return <FormConnector<FormValues> {...this.props} getFormContext={useHookForm} />;
  }
}

export function useForm<FormValues extends FieldValues = FieldValues>(props: FormProps<FormValues>) {
  const context = useHookForm<FormValues>(props);
  const { defaultValues, resetOnDefaultValuesChange } = props;
  const latestProps = useUpdatedRef(props);

  const FormComponent = useMemo(() => {
    class GenericForm extends Form<FormValues, true> {
      public static displayName = `${props.id || ''}Form`;

      render() {
        return <FormConnector<FormValues> {...this.props} {...latestProps.current} getFormContext={() => context} />;
      }
    }

    return GenericForm;
  }, []);

  useEffect(() => {
    if (resetOnDefaultValuesChange && defaultValues) {
      context.reset({ ...defaultValues });
    }
  }, [defaultValues, resetOnDefaultValuesChange]);

  return { Form: FormComponent, ...context };
}
